/**
 * Global SVG filter definitions for the "liquid glass" effect.
 *
 * Mounted once near the root of `WorkspaceShell`. The filter is then
 * referenced from CSS via `backdrop-filter: url(#liquid-glass-pill)`
 * (Chrome-only — Safari + Firefox fall back to a plain blur/saturate
 * backdrop, see `app/globals.css :: .liquid-glass`).
 *
 * The filter approximates Apple's iOS 26 / macOS 26 Liquid Glass by
 * driving an `feDisplacementMap` from a small inline SVG image whose
 * red & green channels encode horizontal & vertical pixel offsets:
 *
 *   - Red 128 / Green 128         → no displacement (neutral)
 *   - Red 0   → -1 X (pull left)  | Red 255   → +1 X (push right)
 *   - Green 0 → -1 Y (pull up)    | Green 255 → +1 Y (push down)
 *
 * Two linear gradients (one per axis) painted with `preserveAspect
 * Ratio='none'` stretch to fill ANY element regardless of size, so a
 * single filter can refract a 40px round FAB, a 600px wide search
 * pill, AND a widget header without per-instance maps. The gradients
 * are concentrated near the edges (a soft S-curve via stops at 0%,
 * 18%, 50%, 82%, 100%) so the middle stays optically clear and only
 * the bezel curves bend the backdrop — exactly how Apple's glass
 * reads to the eye.
 *
 * A second filter (`liquid-glass-pill-active`) pushes the same
 * displacement ~70% harder for the FAB's open/pressed state — the
 * bezel curve visibly deepens when the user toggles a panel, but
 * the surface keeps refracting (no idle vs pressed split where the
 * glass disappears in one state).
 *
 * Performance: filters reuse the same `<feImage>` displacement map
 * via `result="map"`. Chrome composites these on the GPU; 10–20
 * elements per page is well within the budget.
 */
export function LiquidGlassDefs() {
  // Encoded twice — once as a `data:` SVG for `feImage`, once verbatim
  // below. We keep the data URI inline so the build doesn't depend on
  // an external asset for a deeply-internal visual primitive.
  //
  // The displacement map: two perpendicular linear gradients overlaid
  // with `multiply` so they combine into a single pseudo-radial.
  // Gradient stops are an aggressive S-curve concentrated in the
  // outer 25% of each axis — most of the surface stays near-neutral
  // (128/128, no displacement) while the bezel band refracts the
  // backdrop sharply. This is what makes the effect read as "curved
  // glass" rather than "blurred backdrop". Tuned against Apple's
  // iOS 26 reference videos: bezel band is roughly 18–22% of the
  // element's shortest side, with displacement ramping from neutral
  // to extreme over the outer 15%.
  const mapSvg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'>
      <defs>
        <linearGradient id='gx' x1='0' y1='0.5' x2='1' y2='0.5'>
          <stop offset='0%' stop-color='rgb(0,128,128)'/>
          <stop offset='8%' stop-color='rgb(40,128,128)'/>
          <stop offset='22%' stop-color='rgb(108,128,128)'/>
          <stop offset='35%' stop-color='rgb(124,128,128)'/>
          <stop offset='50%' stop-color='rgb(128,128,128)'/>
          <stop offset='65%' stop-color='rgb(132,128,128)'/>
          <stop offset='78%' stop-color='rgb(148,128,128)'/>
          <stop offset='92%' stop-color='rgb(216,128,128)'/>
          <stop offset='100%' stop-color='rgb(255,128,128)'/>
        </linearGradient>
        <linearGradient id='gy' x1='0.5' y1='0' x2='0.5' y2='1'>
          <stop offset='0%' stop-color='rgb(128,0,128)'/>
          <stop offset='8%' stop-color='rgb(128,40,128)'/>
          <stop offset='22%' stop-color='rgb(128,108,128)'/>
          <stop offset='35%' stop-color='rgb(128,124,128)'/>
          <stop offset='50%' stop-color='rgb(128,128,128)'/>
          <stop offset='65%' stop-color='rgb(128,132,128)'/>
          <stop offset='78%' stop-color='rgb(128,148,128)'/>
          <stop offset='92%' stop-color='rgb(128,216,128)'/>
          <stop offset='100%' stop-color='rgb(128,255,128)'/>
        </linearGradient>
      </defs>
      <rect width='100' height='100' fill='url(#gx)'/>
      <rect width='100' height='100' fill='url(#gy)' style='mix-blend-mode:multiply'/>
    </svg>`,
  );
  const mapHref = `data:image/svg+xml;utf8,${mapSvg}`;

  return (
    <svg
      aria-hidden
      width="0"
      height="0"
      style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}
    >
      <defs>
        {/* Pill / panel — subtle, low-scale displacement. Suits the
            search bar, dock, zoom toolbar, widget headers. */}
        <filter
          id="liquid-glass-pill"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
          colorInterpolationFilters="sRGB"
        >
          <feImage
            href={mapHref}
            x="0"
            y="0"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            result="map"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale="12"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>

        {/*
         * Active variant — same map, ~70% stronger displacement so
         * the bezel curve reads as MORE pronounced when the FAB or
         * pill is in its open/pressed state. Paired with a touch
         * more `backdrop-filter` blur + saturation in CSS so the
         * press feels like the glass briefly "deepens" — a subtle
         * but perceptible second pattern of refraction.
         *
         * All four workspace FABs (Create, Activity, Notifications,
         * Brand) consume this via `.liquid-glass.ws-fab-pressed`.
         * No separate FAB filter — the harmonized chrome uses one
         * idle + one active pattern across the board.
         */}
        <filter
          id="liquid-glass-pill-active"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
          colorInterpolationFilters="sRGB"
        >
          <feImage
            href={mapHref}
            x="0"
            y="0"
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            result="map"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="map"
            scale="20"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}
